/**
 * Builders that turn a campus / student / content record into the generic
 * `ModalData` shape consumed by <DrillDownModal>. Kept out of the container so
 * the modal stays fully presentational.
 */

import {
  CampusDatum,
  StudentDatum,
  TeacherDatum,
  CAMPUS_DATA,
  GENRE_DATA,
  LANGUAGE_DATA,
  CONTENT_SUMMARY,
  fmt,
  pct,
} from "../data";

export interface ModalStat {
  value: string | number;
  label: string;
}

export interface ModalStat2 extends ModalStat {
  color: string;
}

export interface ModalRow {
  rank: number;
  name: string;
  hasSub: boolean;
  sub?: string;
  value: string | number;
  bar: string;
}

export interface ModalTab {
  label: string;
  listTitle: string;
  rows: ModalRow[];
}

export interface ModalProfileLink {
  /** School-panel path of the record's own page (no locale prefix). */
  href: string;
  label: string;
}

/** Everything the drill-down needs to print a top-performer certificate. */
export interface ModalCertificate {
  award: string;
  name: string;
  meta?: string;
  achievement: string;
  win?: { from?: string; to?: string };
}

export interface ModalData {
  kind: "campus" | "student" | "content";
  cert: boolean;
  /** Deep link to the record's full profile page, when it has one. */
  profile?: ModalProfileLink;
  /** Set on Top Students / Top Teachers drill-downs so the footer can print
   *  a certificate for that person. */
  certificate?: ModalCertificate;
  title: string;
  subtitle: string;
  stats: ModalStat[];
  stats2: ModalStat2[];
  tabs: ModalTab[];
}

/**
 * Deep links out of a drill-down to the record's own page.
 *
 * In the school panel these point at that school's student / teacher / campus
 * pages. The admin panel has no equivalent screens, so every link resolves to
 * `undefined` and the drill-down simply omits its "View Complete ..." button.
 */
const noLink = (_id?: number | string | null): ModalProfileLink | undefined =>
  undefined;

/** Top Students / Top Teachers -> the person's profile page. */
export const studentProfileLink = noLink;
export const teacherProfileLink = noLink;

/** The Reader Campuses / The Reader Classrooms -> that record's detail page. */
export const campusDetailLink = noLink;
export const classroomLink = noLink;

/** Teacher-panel variants of the two links above. */
export const teacherClassroomLink = noLink;
export const teacherStudentProfileLink = noLink;

/**
 * Certificate payloads for the Top Students / Top Teachers drill-downs. The
 * wording lives here so all three dashboards print the same citation.
 */
export const studentCertificate = (
  s: StudentDatum,
  win?: { from?: string; to?: string },
): ModalCertificate => ({
  award: "Top Reader",
  name: s.name,
  meta: s.meta,
  achievement: `reading with ${fmt(s.books)} ${
    s.books === 1 ? "book" : "books"
  } completed and ${fmt(s.badges)} ${
    s.badges === 1 ? "badge" : "badges"
  } earned`,
  win,
});

export const teacherCertificate = (
  t: TeacherDatum,
  win?: { from?: string; to?: string },
): ModalCertificate => ({
  award: "Top Teacher",
  name: t.name,
  meta: t.campus,
  achievement: `teaching with ${fmt(t.completed)} of ${fmt(
    t.assigned,
  )} assigned books completed and a ${t.quiz}% quiz pass rate`,
  win,
});

export const campusCertificate = (
  c: CampusDatum,
  win?: { from?: string; to?: string },
): ModalCertificate => ({
  award: "Top Campus",
  name: c.name,
  meta: `${fmt(c.classes)} ${
    c.classes === 1 ? "classroom" : "classrooms"
  } · ${fmt(c.students)} ${c.students === 1 ? "student" : "students"}`,
  achievement: `reading with ${fmt(c.books)} ${
    c.books === 1 ? "book" : "books"
  } completed across the campus`,
  win,
});

export const classroomCertificate = (
  c: CampusDatum,
  win?: { from?: string; to?: string },
): ModalCertificate => ({
  award: "Top Classroom",
  name: c.name,
  // the class teacher when the row carries one, otherwise the class size
  meta:
    c.teacher ||
    `${fmt(c.students)} ${c.students === 1 ? "student" : "students"}`,
  achievement: `reading with ${fmt(c.books)} ${
    c.books === 1 ? "book" : "books"
  } completed by ${fmt(c.students)} ${
    c.students === 1 ? "student" : "students"
  }`,
  win,
});

export function buildCampusModal(c: CampusDatum): ModalData {
  const maxC = c.classesTop[0][1];
  const maxS = c.studentsTop[0][1];
  const maxT = c.teachersTop[0][2];
  return {
    kind: "campus",
    cert: true,
    title: c.name,
    subtitle: `${c.classes} classrooms · ${fmt(c.students)} students · ${c.adoption}% adoption`,
    stats: [
      { value: fmt(c.books), label: "Books read" },
      { value: c.adoption + "%", label: "Adoption" },
      { value: c.quiz + "%", label: "Quiz pass" },
    ],
    stats2: [
      { value: fmt(c.assigned), label: "Assigned books", color: "var(--ho-ink)" },
      { value: fmt(c.inProgress), label: "In progress", color: "#1897A6" },
      { value: c.overdue, label: "Overdue", color: "#B42318" },
    ],
    tabs: [
      {
        label: "Classrooms",
        listTitle: "The Reader Classrooms",
        rows: c.classesTop.map((r, i) => ({
          rank: i + 1,
          name: r[0],
          hasSub: false,
          value: fmt(r[1]),
          bar: pct(r[1], maxC),
        })),
      },
      {
        label: "Students",
        listTitle: "The Reader Students",
        rows: c.studentsTop.map((r, i) => ({
          rank: i + 1,
          name: r[0],
          hasSub: false,
          value: r[1] + " books",
          bar: pct(r[1], maxS),
        })),
      },
      {
        label: "Teachers",
        listTitle: "Teacher Performance",
        rows: c.teachersTop.map((r, i) => ({
          rank: i + 1,
          name: r[0],
          hasSub: true,
          sub: `${r[2]}/${r[1]} completed · ${r[3]}% quiz`,
          value: Math.round((r[2] / r[1]) * 100) + "%",
          bar: pct(r[2], maxT),
        })),
      },
    ],
  };
}

/**
 * Teacher drill-down built from the Top Teachers row (there is no per-teacher
 * detail endpoint), so it shows assigned/completed books, completion rate and
 * quiz pass — the same figures the row already carries.
 */
export function buildTeacherModal(t: TeacherDatum): ModalData {
  const assigned = Number(t.assigned) || 0;
  const completed = Number(t.completed) || 0;
  const completionRate = assigned
    ? Math.round((completed / assigned) * 100)
    : 0;
  return {
    kind: "campus",
    cert: false,
    title: t.name,
    subtitle: t.campus,
    stats: [
      { value: fmt(completed), label: "Books completed" },
      { value: fmt(assigned), label: "Books assigned" },
      { value: t.quiz + "%", label: "Quiz pass" },
    ],
    stats2: [],
    tabs: [
      {
        label: "Performance",
        listTitle: "Teaching performance",
        rows: [
          {
            rank: 1,
            name: "Books completed",
            hasSub: true,
            sub: `of ${fmt(assigned)} assigned`,
            value: fmt(completed),
            bar: pct(completed, assigned || 1),
          },
          {
            rank: 2,
            name: "Completion rate",
            hasSub: false,
            value: completionRate + "%",
            bar: completionRate + "%",
          },
          {
            rank: 3,
            name: "Quiz pass rate",
            hasSub: false,
            value: t.quiz + "%",
            bar: t.quiz + "%",
          },
        ],
      },
    ],
  };
}

export function buildStudentModal(s: StudentDatum): ModalData {
  return {
    kind: "student",
    cert: false,
    title: s.name,
    subtitle: s.meta,
    // fallback shape (detail API unavailable): only the fields the top-students
    // list actually returns — reading time / quiz / pages are not in that payload
    stats: [
      { value: fmt(s.books), label: "Books read" },
      { value: fmt(s.badges), label: "Badges earned" },
    ],
    stats2: [],
    tabs: [
      {
        label: "Progress",
        listTitle: "Progress highlights",
        rows: [
          { rank: 1, name: "Books completed", hasSub: false, value: fmt(s.books), bar: "100%" },
          { rank: 2, name: "Badges earned", hasSub: false, value: fmt(s.badges), bar: pct(s.badges, Math.max(1, s.badges)) },
        ],
      },
    ],
  };
}

export function buildContentModal(): ModalData {
  const maxB = Math.max(...CAMPUS_DATA.map((c) => c.books));
  const maxG = GENRE_DATA[0].pct;
  const maxL = LANGUAGE_DATA[0].pct;
  return {
    kind: "content",
    cert: false,
    title: "Books & Content Analytics",
    subtitle: "What the network reads, by whom, and whether it fits",
    stats: [
      { value: CONTENT_SUMMARY.overallBooks, label: "Overall books Read" },
      { value: CONTENT_SUMMARY.ageAppropriate, label: "Age-appropriate" },
      { value: CONTENT_SUMMARY.mostReadGenre.name, label: "Most-read genre" },
    ],
    stats2: [],
    tabs: [
      {
        label: "By campus",
        listTitle: "Completions by campus",
        rows: CAMPUS_DATA.map((c, i) => ({
          rank: i + 1,
          name: c.name,
          hasSub: false,
          value: fmt(c.books),
          bar: pct(c.books, maxB),
        })),
      },
      {
        label: "By genre",
        listTitle: "Completions by genre",
        rows: GENRE_DATA.map((g, i) => ({
          rank: i + 1,
          name: g.name,
          hasSub: false,
          value: g.pct + "%",
          bar: pct(g.pct, maxG),
        })),
      },
      {
        label: "By language",
        listTitle: "Completions by language",
        rows: LANGUAGE_DATA.map((l, i) => ({
          rank: i + 1,
          name: l.name,
          hasSub: false,
          value: l.pct + "%",
          bar: pct(l.pct, maxL),
        })),
      },
    ],
  };
}
